/**
 * Verzioniranje shranjene oblike (T2 §4).
 *
 * Stara aplikacija je imela en ključ `ds72radio` **brez verzije** — vsaka
 * sprememba sheme je bila zato ugibanje, kaj v shrambi sploh je. Zato je
 * verzija tu od prvega dne, čeprav migracij še ni.
 *
 * Migracije so čiste funkcije nad posnetkom celotne shrambe, ne nad
 * posameznim ključem: sprememba sheme se rada dotakne dveh naenkrat
 * (npr. polje se preseli iz nastavitev v postajo) in dve neodvisni
 * migraciji tega ne moreta opraviti brez vmesnega, neveljavnega stanja.
 */

export const SCHEMA_VERSION = 2;

/** Posnetek vsega, kar gre skozi migracije. Namerno neopredeljen. */
export type StoredSnapshot = Record<string, unknown>;

type Migration = (data: StoredSnapshot) => StoredSnapshot;

/**
 * Izbriše zapis `metaCapability`, ki pravi »ta postaja nima metapodatkov«.
 *
 * Zapis je do te različice nastal tudi iz **minljivega** izpada omrežja: trije
 * neuspeli polli so sprožili ponovno zaznavo, ta je ob še vedno slabi povezavi
 * padla in izid `none` se je zapisal kot dejstvo. `none` pomeni »ne poizveduj
 * več«, zato se postaja ni mogla popraviti niti po ponovnem zagonu — naslov in
 * ovitek sta obstala pri zadnji uspešni skladbi za 30 dni.
 *
 * Vzrok je popravljen v kaskadi, ti zapisi pa so že v shrambah in jih popravek
 * sam ne doseže. Zato jih migracija odstrani; postaje se ob prvem predvajanju
 * zaznajo znova.
 *
 * ⚠️ Odstranijo se **vsi** `none`, tudi pravilni. Cena je ena zaznava na
 * postajo; razlikovati pravilnega od zastrupljenega ni po čem.
 */
function dropNoneCapability(station: unknown): unknown {
  if (typeof station !== 'object' || station === null) return station;

  const record = station as Record<string, unknown>;
  const capability = record.metaCapability;
  if (typeof capability !== 'object' || capability === null) return station;
  if ((capability as { kind?: unknown }).kind !== 'none') return station;

  const { metaCapability: _capability, metaCheckedAt: _checkedAt, ...rest } = record;
  return rest;
}

/**
 * Ključ `n` pretvori verzijo `n` v verzijo `n + 1`.
 */
const MIGRATIONS: Record<number, Migration> = {
  1: (data) => {
    if (!Array.isArray(data.stations)) return data;
    return { ...data, stations: data.stations.map(dropNoneCapability) };
  },
};

export type MigrationResult =
  | { ok: true; data: StoredSnapshot; migrated: boolean }
  | { ok: false; from: number; failedAt: number; cause: unknown };

/**
 * Migracije od `from` do `SCHEMA_VERSION`, zaporedno.
 *
 * Ob napaki se **ne zapiše nič** (T2 §4, pravilo 4). Delno migriran zapis je
 * slabši od starega: star znamo prebrati, delno migriranega ne zna nihče —
 * niti prihodnja migracija, ki bo predpostavila celoto.
 */
export function migrate(data: StoredSnapshot, from: number): MigrationResult {
  if (from > SCHEMA_VERSION) {
    // Shrambo je zapisala novejša verzija aplikacije. Ugibanje bi podatke
    // pokvarilo; branje odpovemo in uporabniku ostane izvoz.
    return {
      ok: false,
      from,
      failedAt: from,
      cause: new Error('Shramba je novejša od aplikacije'),
    };
  }

  let current = data;
  for (let version = from; version < SCHEMA_VERSION; version += 1) {
    const step = MIGRATIONS[version];
    if (step === undefined) {
      return {
        ok: false,
        from,
        failedAt: version,
        cause: new Error(`Manjka migracija ${version}`),
      };
    }
    try {
      current = step(current);
    } catch (cause) {
      return { ok: false, from, failedAt: version, cause };
    }
  }

  return { ok: true, data: current, migrated: from !== SCHEMA_VERSION };
}
